// API helper functions for account sagas
import gql from 'graphql-tag';
import client from '@api';
import {
  checkUsername,
  resetPasswordMutation,
  signIn,
  signUpMutation,
} from '../../graphql/mutations/account';
import type { SignInPayload, SignUpPayload, UsernamePayload } from './types';

/**
 * Copy only the listed keys that carry a value. The GraphQL server rejects a
 * whole operation over a single unknown input field ("Unknown field"), so
 * anything the UI tacks onto an action payload must never reach `input`.
 */
const pickDefined = <K extends string>(
  source: Record<string, unknown>,
  keys: readonly K[],
): Partial<Record<K, unknown>> =>
  keys.reduce<Partial<Record<K, unknown>>>((acc, key) => {
    if (source[key] !== undefined && source[key] !== null) {
      acc[key] = source[key];
    }
    return acc;
  }, {});

/** Fields of `AccountSignInInput` (prod schema). */
export const SIGN_IN_INPUT_FIELDS = ['email', 'password', 'urlParams'] as const;

/** Fields of `AccountSignUpInput` (prod schema). */
export const SIGN_UP_INPUT_FIELDS = [
  'email',
  'password',
  'phoneNumber',
  'organizationInvitationToken',
  'urlParams',
] as const;

export const toSignInInput = (payload: SignInPayload) =>
  pickDefined(payload, SIGN_IN_INPUT_FIELDS);

export const toSignUpInput = (
  payload: SignUpPayload & { organizationInvitationToken?: string },
) => pickDefined(payload, SIGN_UP_INPUT_FIELDS);

export const mutateSignIn = (accountInput: SignInPayload): Promise<unknown> =>
  client().mutate({
    mutation: signIn,
    variables: { input: toSignInInput(accountInput) },
  });

export const mutateSignUp = (
  accountInput: SignUpPayload & { organizationInvitationToken?: string },
): Promise<unknown> =>
  client().mutate({
    mutation: signUpMutation,
    variables: { input: toSignUpInput(accountInput) },
  });

/** `token` is the `:challenge` from the `/auth/reset-password/:challenge` email link. */
export const mutateResetPassword = (
  token: string,
  newPassword: string,
): Promise<unknown> =>
  client().mutate({
    mutation: resetPasswordMutation,
    variables: { token, input: { newPassword } },
  });

export const mutateCheckUsername = (input: UsernamePayload): Promise<unknown> =>
  client().mutate({
    mutation: checkUsername,
    variables: { input },
  });

const resendVerification = gql`
  mutation ResendVerificationOnSignIn(
    $input: AccountResendVerificationEmailInput!
  ) {
    accountContactVerificationResend(input: $input)
  }
`;

/** Sends a fresh sign-up code; answers true even for unknown emails. */
export const mutateResendVerification = (email: string): Promise<unknown> =>
  client().mutate({
    mutation: resendVerification,
    variables: { input: { email } },
  });
